import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { emailValidation } from '@sv-support-sdk/utils/src/helpers/validations';
import List from '@sv-support-sdk/ui-components/src/components/Lists/List';
import ActionsItem from '@sv-support-sdk/ui-components/src/components/ControlLayout/Actions/ActionsItem';
import Actions from '@sv-support-sdk/ui-components/src/components/ControlLayout/Actions';
import DividerComponent from '@sv-support-sdk/ui-components/src/components/Content/Divider';

import MessagePlaceholder from '../../ui/Content/Message/MessagePlaceholder';
import Headline from '../../ui/Typography/Headline';
import Paragraph from '../../ui/Typography/Paragraph';
import Input from '../../ui/Controls/Input';
import Textarea from '../../ui/Controls/Textarea';
import Button from '../../ui/Controls/Button';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectUserEmail, selectUserId, selectWorkspaceId } from '../../../store/selectors/user';
import { createTicket } from '../../../store/actions/ticket';
import { ObjectOfBool, ObjectStrings } from '../../../types/commonTypes';
import { SUPPORT_ROUTE } from '../../../config';
import { $l } from '../../../locales';
import { redirectToSupport } from '../../../store/actions/chat';
import setQaAttribute from '../../../helpers/setQaAttribute';
import {
  CHAT_TICKET_EMAIL_INPUT,
  CHAT_TICKET_GO_TO_HELP_CENTER,
  CHAT_TICKET_MESSAGE_INPUT,
  CHAT_TICKET_SEND,
  CHAT_TICKET_SUBJECT_INPUT,
} from '../../../constants/qaAttributes';
import { TICKET_MESSAGE_ID } from '../../../constants/messages';
import { requestDemo } from '../../../helpers/common';

const Ticket = () => {
  const dispatch = useAppDispatch();
  const id = useAppSelector(selectUserId);
  const email = useAppSelector(selectUserEmail);
  const workspaceId = useAppSelector(selectWorkspaceId);
  const userEmail = emailValidation(email) ? email : '';
  const [ticketData, setTicketData] = useState<ObjectStrings>({
    email: userEmail || '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const [blur, setBlur] = useState<ObjectOfBool>({
    email: false,
    message: false,
    subject: false,
  });

  const isSupportPage = window.location.origin === SUPPORT_ROUTE;
  const goToHelpCenter = useCallback(() => {
    redirectToSupport(workspaceId)();
  }, [workspaceId]);

  const errors = useMemo(
    () => ({
      email: !emailValidation(ticketData.email as string),
      message: !ticketData.message,
      subject: !ticketData.subject,
    }),
    [ticketData],
  );

  const onBlur = ({ target: { name = '' } }) => {
    setBlur((state) => ({ ...state, [name]: true }));
  };

  useEffect(() => {
    setTicketData((state) => ({ ...state, email: userEmail }));
  }, [userEmail]);

  const onChange = ({ target: { value = '', name = '' } }) => {
    setTicketData((state) => ({ ...state, [name]: value }));
  };

  const sendTicket = async () => {
    setBlur((state) => Object.keys(state).reduce((acc, el) => ({ ...acc, [el]: true }), {}));

    if (Object.values(errors).some((el) => el)) return;

    try {
      setLoading(true);
      await dispatch(
        createTicket({
          ...ticketData,
          id,
          org_id: workspaceId || undefined,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <MessagePlaceholder id={TICKET_MESSAGE_ID}>
      <List gap="16" width="full">
        <List gap="4">
          <Headline type="secondary" fontSize="16">
            {$l('TICKET_CREATE_HEADER')}
          </Headline>
          <Paragraph fontSize="12">{$l('TICKET_CREATE_TEXT')}</Paragraph>
        </List>
        <Input
          autoFocus
          name="email"
          placeholder={$l('ENTER_YOUR_EMAIL')}
          disabled={!!userEmail}
          onChange={onChange}
          onBlur={onBlur}
          isInvalid={blur.email && errors.email}
          value={ticketData.email}
          {...setQaAttribute(CHAT_TICKET_EMAIL_INPUT)}
        />
        <Input
          autoFocus={!!userEmail}
          placeholder={$l('ENTER_A_SUBJECT')}
          name="subject"
          onChange={onChange}
          onBlur={onBlur}
          isInvalid={blur.subject && errors.subject}
          value={ticketData.subject}
          {...setQaAttribute(CHAT_TICKET_SUBJECT_INPUT)}
        />
        <Textarea
          placeholder={$l('ENTER_YOUR_MESSAGE')}
          name="message"
          onChange={onChange}
          onBlur={onBlur}
          isInvalid={blur.message && errors.message}
          value={ticketData.message}
          {...setQaAttribute(CHAT_TICKET_MESSAGE_INPUT)}
        />
        <Actions width="full" gap="16" direction="column">
          <ActionsItem width="full">
            <Button
              text={$l('SEND')}
              disabled={loading}
              onClick={sendTicket}
              {...setQaAttribute(CHAT_TICKET_SEND)}
            />
          </ActionsItem>
          <DividerComponent />
          <Actions width="full" gap="8">
            {!isSupportPage && (
              <ActionsItem width="full">
                <Button
                  variant="primary-outlined"
                  size="small"
                  radius="20"
                  text={$l('GO_TO_HELP_CENTER')}
                  disabled={loading}
                  {...setQaAttribute(CHAT_TICKET_GO_TO_HELP_CENTER)}
                  onClick={goToHelpCenter}
                />
              </ActionsItem>
            )}
            <ActionsItem width="full">
              <Button
                variant="primary-outlined"
                text={$l('REQUEST_A_DEMO')}
                size="small"
                radius="20"
                disabled={loading}
                onClick={requestDemo}
              />
            </ActionsItem>
          </Actions>
        </Actions>
      </List>
    </MessagePlaceholder>
  );
};

export default Ticket;
